import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, ProgressBar } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import eventBus from "../utils/EventBus";
import { backendApis } from "../config";
import { useParams } from "react-router-dom";
import { waitFor } from "../utils/helpers";
import { useBackendUrl } from "../utils/hooks";

// ─── AttachmentsModal ─────────────────────────────────────────────────────────

function AttachmentsModal({
  show,
  onClose,
  onUploaded,
  backendUrl,
  initialFilesRef,
}) {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [retryingKeys, setRetryingKeys] = useState(new Set());
  const [uploading, setUploading] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [allDone, setAllDone] = useState(false);

  const rawFilesRef = useRef(new Map());
  const didInitUpload = useRef(false);

  // ── auto-start uploads for files chosen before modal opened ───────────────

  useEffect(() => {
    if (!show || didInitUpload.current) return;
    const pending = initialFilesRef.current ?? [];
    if (!pending.length) return;
    didInitUpload.current = true;
    initialFilesRef.current = [];
    uploadBatch(pending, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // ── derived ────────────────────────────────────────────────────────────────

  const anyBusy = uploading || retryingKeys.size > 0;
  const hasFailures = files.some(f => f.failed);
  const canClose = !anyBusy && !hasFailures;

  const activeUploads = Object.entries(uploadProgress).filter(
    ([, p]) => p.status === "uploading"
  );

  // ── helpers ────────────────────────────────────────────────────────────────

  const setProgress = useCallback((key, status, progress = 0) => {
    setUploadProgress(prev => ({ ...prev, [key]: { status, progress } }));
  }, []);

  const maybeNotify = useCallback(fileList => {
    if (fileList.length > 0 && !fileList.some(f => f.failed)) {
      setAllDone(true);
    }
  }, []);

  // ── upload one file via XHR (real progress) ────────────────────────────────

  const uploadOne = useCallback(
    async (file, key) => {
      rawFilesRef.current.set(key, file);
      setProgress(key, "uploading", 0);

      const formData = new FormData();
      formData.append("file", file);

      try {
        const uploaded = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.upload.addEventListener("progress", e => {
            const pct = e.lengthComputable
              ? Math.round((e.loaded / e.total) * 100)
              : 0;
            setProgress(key, "uploading", pct);
          });

          xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText));
              } catch {
                reject(new Error("Invalid JSON response"));
              }
            } else {
              reject(new Error(`${xhr.status} ${xhr.statusText}`));
            }
          });

          xhr.addEventListener("error", () =>
            reject(new Error("Network error"))
          );
          xhr.addEventListener("abort", () =>
            reject(new Error("Upload aborted"))
          );

          xhr.open("POST", `${backendUrl}/api/forms/files/upload`);
          xhr.send(formData);
        });

        setProgress(key, "completed", 100);
        rawFilesRef.current.delete(key);

        return {
          fileId: uploaded.fileId,
          name: uploaded.originalName || file.name,
          systemFilename: uploaded.systemFilename,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString(),
          url: uploaded.url,
          isUploaded: true,
        };
      } catch (err) {
        setProgress(key, "failed", 0);
        return {
          fileKey: key,
          name: file.name,
          error: err.message ?? "Upload failed",
          size: file.size,
          type: file.type,
          failed: true,
        };
      }
    },
    [backendUrl, setProgress]
  );

  // ── batch upload ───────────────────────────────────────────────────────────

  const uploadBatch = useCallback(
    async (rawFiles, existingFiles) => {
      setUploading(true);
      setGlobalError("");

      const keyed = Array.from(rawFiles).map((file, i) => ({
        file,
        key: `upload_${Date.now()}_${i}`,
      }));

      const results = await Promise.all(
        keyed.map(({ file, key }) => uploadOne(file, key))
      );

      const next = [...existingFiles, ...results];
      setFiles(next);

      const failCount = results.filter(r => r.failed).length;
      if (failCount > 0) {
        setGlobalError(
          `${failCount} file${
            failCount > 1 ? "s" : ""
          } failed. Retry or remove them to continue.`
        );
      } else {
        maybeNotify(next);
      }

      setUploading(false);

      setTimeout(() => {
        setUploadProgress(prev => {
          const n = { ...prev };
          Object.keys(n).forEach(k => {
            if (n[k].status === "completed") delete n[k];
          });
          return n;
        });
      }, 2000);
    },
    [uploadOne, maybeNotify]
  );

  // ── add more files from inside the modal ──────────────────────────────────

  const handleFileSelect = e => {
    if (e.target.files?.length) uploadBatch(e.target.files, files);
    e.target.value = "";
  };

  // ── retry ──────────────────────────────────────────────────────────────────

  const retryFile = useCallback(
    async (failedFile, index) => {
      const original = rawFilesRef.current.get(failedFile.fileKey);
      if (!original) {
        setGlobalError("Original file not found — please re-add it.");
        return;
      }

      const newKey = `retry_${uuidv4()}`;
      setRetryingKeys(prev => new Set([...prev, failedFile.fileKey]));

      const result = await uploadOne(original, newKey);

      setFiles(prev => {
        const next = [...prev];
        next[index] = result;

        if (!result.failed) {
          rawFilesRef.current.delete(failedFile.fileKey);
          const remaining = next.filter(f => f.failed).length;
          if (remaining === 0) {
            setGlobalError("");
            maybeNotify(next);
          } else {
            setGlobalError(
              `${remaining} file(s) still failing. Retry or remove them to continue.`
            );
          }
        }

        return next;
      });

      setRetryingKeys(prev => {
        const s = new Set(prev);
        s.delete(failedFile.fileKey);
        return s;
      });
    },
    [uploadOne, maybeNotify]
  );

  // ── delete ─────────────────────────────────────────────────────────────────

  const deleteFile = useCallback(
    async (file, index) => {
      if (file.fileId) {
        try {
          const res = await fetch(
            `${backendUrl}/api/forms/files/${file.fileId}`,
            { method: "DELETE", credentials: "include" }
          );
          if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        } catch (err) {
          setGlobalError(`Delete failed: ${err.message}`);
          return;
        }
      }

      if (file.fileKey) rawFilesRef.current.delete(file.fileKey);

      setFiles(prev => {
        const next = prev.filter((_, i) => i !== index);
        const remaining = next.filter(f => f.failed).length;
        if (remaining === 0) {
          setGlobalError("");
          maybeNotify(next);
        } else {
          setGlobalError(
            `${remaining} file(s) still failing. Retry or remove them to continue.`
          );
        }
        return next;
      });
    },
    [backendUrl, maybeNotify]
  );

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered={true}
      animation={false}
      enforceFocus={false}
      backdrop={canClose ? true : "static"}
      keyboard={canClose}
    >
      <Modal.Header closeButton={canClose}>
        <Modal.Title className="h5">Uploads</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {globalError && (
          <div
            className="alert alert-warning alert-dismissible mb-3"
            role="alert"
          >
            {globalError}
            <button
              type="button"
              className="btn-close"
              aria-label="Dismiss"
              onClick={() => setGlobalError("")}
            />
          </div>
        )}

        {activeUploads.length > 0 && (
          <div className="mb-3">
            <p className="small fw-semibold mb-1">Uploading…</p>
            {activeUploads.map(([key, p]) => (
              <div key={key} className="mb-2">
                <div className="d-flex justify-content-between">
                  <small className="text-muted text-truncate">
                    {rawFilesRef.current.get(key)?.name ?? key}
                  </small>
                  <small className="text-muted ms-2">{p.progress}%</small>
                </div>
                <ProgressBar now={p.progress} style={{ height: 6 }} />
              </div>
            ))}
          </div>
        )}

        {files.length > 0 && (
          <ul className="list-unstyled mb-3">
            {files.map((file, index) => {
              const isRetrying = retryingKeys.has(file.fileKey);
              return (
                <li
                  key={file.fileId ?? file.fileKey ?? index}
                  className="d-flex align-items-center justify-content-between p-2 mb-2 border rounded"
                >
                  <div className="flex-grow-1 me-2 overflow-hidden">
                    <span className="text-truncate d-block">
                      {file.name}
                      {file.error && (
                        <small className="d-block text-danger">
                          {file.error}
                        </small>
                      )}
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    {file.failed && (
                      <span className="badge bg-danger">Failed</span>
                    )}

                    {file.isUploaded && (
                      <span className="badge bg-success">Uploaded</span>
                    )}

                    {file.failed && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => retryFile(file, index)}
                        disabled={isRetrying || anyBusy}
                      >
                        {isRetrying ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            />
                            Retrying…
                          </>
                        ) : (
                          "↻ Retry"
                        )}
                      </Button>
                    )}

                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => deleteFile(file, index)}
                      disabled={anyBusy}
                    >
                      ✕
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <label
          className={`btn btn-light-blue border${anyBusy ? " disabled" : ""}`}
        >
          {uploading ? "Uploading…" : "Add Files"}
          <input
            type="file"
            className="d-none"
            multiple
            onChange={handleFileSelect}
            disabled={anyBusy}
          />
        </label>
      </Modal.Body>

      <Modal.Footer>
        {hasFailures && (
          <small className="text-danger me-auto">
            Resolve all failed files before closing.
          </small>
        )}
        <Button
          variant={allDone ? "success" : "secondary"}
          onClick={() => (allDone ? onUploaded(files) : onClose())}
          disabled={!canClose}
        >
          {allDone ? "✓ Attach Files" : "Done"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── FileHolder ───────────────────────────────────────────────────────────────

export default function FileHolder() {
  const params = useParams();
  const backendUrlFromHook = useBackendUrl();

  const { url: backendUrl } = params?.company
    ? backendApis?.find(el => el.name === params?.company)
    : { url: backendUrlFromHook };

  const inputRef = useRef();
  const pendingFiles = useRef([]);
  // Everything the listener needs lives in refs — no state — so the
  // listener registered once at mount always reads current values and
  // state changes can never cause a second click.
  const pickerBusy = useRef(false);
  const isMultipleRef = useRef(false);
  const elementToUpdateRef = useRef(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalKey, setModalKey] = useState(0);

  // ── release pickerBusy when the OS file dialog is dismissed without a pick ─

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    const onCancel = () => {
      pickerBusy.current = false;
    };
    input.addEventListener("cancel", onCancel);
    return () => input.removeEventListener("cancel", onCancel);
  }, []);

  // ── postMessage listener — registered ONCE, no deps ──────────────────────

  useEffect(() => {
    const onMessage = e => {
      // console.log("Received message in FileHolder:", e.data);
      const { type, elementId, elementType } = e.data ?? {};

      if (type !== "SELECT_FILE_SINGLE" && type !== "SELECT_FILE_MULTIPLE")
        return;

      if (pickerBusy.current) return;
      pickerBusy.current = true;

      isMultipleRef.current = type === "SELECT_FILE_MULTIPLE";
      elementToUpdateRef.current = { elementId, elementType };

      // Update the input's multiple attr directly — no state, no re-render
      if (inputRef.current) {
        inputRef.current.multiple = isMultipleRef.current;
      }

      setTimeout(() => inputRef.current?.click(), 100);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // ── hidden input → seed modal ─────────────────────────────────────────────

  const handleFileInput = e => {
    const rawFiles = Array.from(e.target.files ?? []);

    if (!rawFiles.length) {
      // User cancelled the OS picker — release guard
      pickerBusy.current = false;
      e.target.value = null;
      return;
    }

    pendingFiles.current = rawFiles;
    setModalKey(k => k + 1);
    setModalOpen(true);
    e.target.value = null;
  };

  // ── called when all files upload successfully ─────────────────────────────

  const handleUploaded = useCallback(
    uploadedFiles => {
      const iframeFiles = uploadedFiles.map((f, i) => {
        const lastDot = f.name.lastIndexOf(".");
        return {
          fileId: f.fileId ?? `${uuidv4()}-${i}`,
          name: lastDot > -1 ? f.name.substring(0, lastDot) : f.name,
          ext: lastDot > -1 ? f.name.substring(lastDot + 1) : "",
          size: f.size,
          url: f.url,
          systemFilename: f.systemFilename,
          isUploaded: true,
        };
      });

      eventBus.dispatch("SEND_FILE_TO_IFRAME", {
        files: iframeFiles,
        ...elementToUpdateRef.current,
      });

      pickerBusy.current = false;
      setModalOpen(false);
    },
    [] // refs never need to be in deps
  );

  // ── close handler ─────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    pickerBusy.current = false;
    setModalOpen(false);
  }, []);

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="position-absolute" style={{ top: "-100vh" }}>
        <input ref={inputRef} type="file" onChange={handleFileInput} />
      </div>

      {modalOpen && (
        <AttachmentsModal
          key={modalKey}
          show={modalOpen}
          onClose={handleClose}
          onUploaded={handleUploaded}
          backendUrl={backendUrl}
          initialFilesRef={pendingFiles}
        />
      )}
    </>
  );
}

export { AttachmentsModal };
